import { Post } from '@/model/data-object/Post'
import useReadStorageManager, {
  TopicReadPointer,
} from '@/composables/read/readStorageManager'
import { ActivePostPage } from '@/composables/ui/profilePostListMenu'
import usePostList from '@/composables/post/postList'
import usePostListMenu from '@/composables/ui/postListMenu'
import useAppState from '@/composables/ui/ui'

let pointer: TopicReadPointer

export default function useReadManager() {
  const { getLastPost, getFirstPost } = usePostList()
  const { firstForumVisit } = useAppState()
  const { getPointer, updatePointer } = useReadStorageManager()
  const { activePage } = usePostListMenu()

  const ensurePointer = () => {
    if (!pointer) {
      pointer = getPointer()
      if (!pointer.fromCache) {
        firstForumVisit.value = true
      }
    }
  }

  ensurePointer()

  const resolveIsNew = (post: Post): boolean => {
    const res =
      (post.path <= pointer.oldestTo && post.path >= pointer.oldestFrom) ||
      (post.pathDesc <= pointer.newestTo && post.pathDesc >= pointer.newestFrom)

    return !res || post.createdAt.getTime() > pointer.lastReadDate
  }

  const updateIsNew = (post: Post) => {
    post.postMeta.isNew = resolveIsNew(post)
  }

  const saveToCache = () => {
    updatePointer(pointer)
  }

  const updateNewestRead = () => {
    const firstPost: Post | undefined = getFirstPost()
    const lastPost: Post | undefined = getLastPost()

    if (firstPost && pointer.newestFrom > firstPost.pathDesc) {
      pointer.newestFrom = firstPost.pathDesc
    }
    if (lastPost && pointer.newestTo < lastPost.pathDesc) {
      pointer.newestTo = lastPost.pathDesc
    }
    saveToCache()
  }

  const updateOldestRead = () => {
    const firstPost: Post | undefined = getFirstPost()
    const lastPost: Post | undefined = getLastPost()

    if (firstPost && pointer.oldestFrom > firstPost.path) {
      pointer.oldestFrom = firstPost.path
    }
    if (lastPost && pointer.oldestTo < lastPost.path) {
      pointer.oldestTo = lastPost.path
    }
    saveToCache()
  }

  const updateReadDate = (): void => {
    if (
      [ActivePostPage.Oldest, ActivePostPage.Show].includes(activePage.value)
    ) {
      pointer.lastReadDate = new Date().getTime()
      updateOldestRead()
    } else if (activePage.value === ActivePostPage.Newest) {
      pointer.lastReadDate = new Date().getTime()
      updateNewestRead()
    }
  }

  return {
    updateIsNew,
    updateReadDate,
  }
}
